import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NewsletterService } from 'src/app/service/newsletter.service';
import { environment } from 'src/environments/environment';

/**
 * Pop-up d'accueil du site (remplace celui de la September Peak Sale) :
 * inscription aux offres exclusives (liste Zoho, comme la page /subscribe)
 * ou discussion avec un membre de l'équipe (Live Helper Chat).
 */
@Component({
  selector: 'app-popup-exclusive-deals',
  templateUrl: './popup-exclusive-deals.component.html',
  styleUrls: ['./popup-exclusive-deals.component.css'],
})
export class PopupExclusiveDealsComponent {
  /** Attente maximale du widget de chat (~8 s de chargement mesurées sur le site) */
  private static readonly CHAT_WAIT_MS = 20000;
  /**
   * Même chat que le widget (base et département du DashboardComponent), en
   * page autonome : utilisée hors production, où le widget ne se charge pas.
   */
  private static readonly CHAT_URL =
    'https://chat.luxuryholidaysdirect.com/chat/start/(department)/1';

  @Input() isVisible: boolean = false;

  signupForm: FormGroup;
  isSubmitting: boolean = false;
  showError: boolean = false;

  isSubmitedOpen: boolean = false;
  submitedTitle: string = 'Submission Confirmed';
  submitedMessage: string = 'Thank you for Signing Up';

  constructor(
    private formBuilder: FormBuilder,
    private newsletter: NewsletterService,
  ) {
    this.signupForm = this.formBuilder.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  closeModal() {
    this.isVisible = false;
  }

  onBackdropClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('overlay')) {
      this.closeModal();
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.signupForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  async subscribe() {
    if (!this.signupForm.valid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.showError = false;
    const { name, email } = this.signupForm.value;

    try {
      await this.newsletter.subscribe(String(name).trim(), email);
      this.signupForm.reset();
      this.closeModal();
      this.isSubmitedOpen = true;
    } catch (error) {
      console.error('Newsletter subscription failed:', error);
      this.showError = true;
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Ouvre le widget du chat chargé par le DashboardComponent (fenêtre
   * « Have a question? Just ask us? »). Il n'est prêt que quelques secondes
   * après l'arrivée sur le site, soit pendant que ce pop-up est affiché : on
   * l'attend.
   * - Production : mêmes événements que le clic sur la bulle Live Helper Chat
   *   en bas à droite, et rien d'autre (pas de fenêtre séparée).
   * - Hors production (staging) : le serveur de chat refuse le domaine, le
   *   widget ne se charge pas ; le chat s'ouvre alors dans sa propre fenêtre.
   */
  openChat() {
    this.closeModal();
    const isProd = environment.envName === 'PROD';
    const startedAt = Date.now();
    const tryOpen = () => {
      const chat = (window as any).$_LHC?.eventListener;
      const timedOut =
        Date.now() - startedAt >= PopupExclusiveDealsComponent.CHAT_WAIT_MS;
      if (chat) {
        chat.emitEvent('showWidget');
        chat.emitEvent('clickAction');
      } else if (
        !isProd &&
        ((window as any).LHC_LOAD_FAILED || timedOut)
      ) {
        window.open(
          PopupExclusiveDealsComponent.CHAT_URL,
          '_blank',
          'noopener,width=500,height=520',
        );
      } else if (!timedOut) {
        setTimeout(tryOpen, 250);
      }
    };
    tryOpen();
  }

  closeSubmited() {
    this.isSubmitedOpen = false;
  }
}
